import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Plus, Trash2, Search, Check, AlertTriangle, X, Mail } from 'lucide-react';
import { ticketService } from '../../services/api';
import { cn } from '../../lib/utils';

const EMPTY_LINE = { ref_vente: '', old_email: '', new_email: '' };

export const EmailUpdateModal = ({ open, onOpenChange, project = 'WORLD CUP 2026', onRefreshData }) => {
  const [lines, setLines] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const updateLine = (idx, field, value) => {
    const newLines = [...lines];
    newLines[idx] = { ...newLines[idx], [field]: value };
    setLines(newLines);
    setPreview(null);
    setResult(null);
  };

  const addLine = () => {
    setLines([...lines, { ...EMPTY_LINE }]);
  };

  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== idx));
    setPreview(null);
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (!text.includes('\t') && !text.includes(';')) return;
    
    e.preventDefault();
    const rows = text.trim().split('\n').map(row => {
      const cols = row.split(/\t|;/).map(c => c.trim());
      return {
        ref_vente: cols[0] || '',
        old_email: cols[1] || '',
        new_email: cols[2] || ''
      };
    }).filter(r => r.ref_vente || r.old_email || r.new_email);
    
    if (rows.length > 0) {
      setLines(rows);
      setPreview(null);
      setResult(null);
    }
  };

  const validLines = lines.filter(l => l.ref_vente.trim() && l.new_email.trim());

  const handlePreview = async () => {
    if (validLines.length === 0) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await ticketService.previewEmailUpdate(validLines, project);
      setPreview(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleApply = async () => {
    if (!preview) return;
    const okLines = preview.results.filter(r => r.status === 'OK');
    if (okLines.length === 0) return;
    
    setApplying(true);
    try {
      const linesToApply = okLines.map(r => ({
        ref_vente: r.ref_vente,
        old_email: r.old_email,
        new_email: r.new_email
      }));
      const data = await ticketService.applyEmailUpdate(linesToApply, project);
      setResult(data);
      setPreview(null);
      onRefreshData?.();
    } catch (err) {
      console.error(err);
    }
    setApplying(false);
  };

  const handleReset = () => {
    setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }, { ...EMPTY_LINE }]);
    setPreview(null);
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Mail className="h-5 w-5 text-sky-400" />
            Mise à jour des emails clients
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-zinc-400 -mt-2">
          Entrez les REF VENTE, l'ancien mail et le nouveau mail. Le système vérifie avant d'appliquer.
          <br />
          <span className="text-zinc-500">Astuce : copier-coller depuis Excel (REF | Ancien mail | Nouveau mail)</span>
        </p>

        {/* Input Lines */}
        {!preview && !result && (
          <div className="space-y-2" data-testid="email-update-lines">
            {/* Header */}
            <div className="grid grid-cols-[1fr_1.5fr_1.5fr_32px] gap-2 text-xs text-zinc-500 font-medium px-1">
              <span>REF VENTE</span>
              <span>ANCIEN MAIL</span>
              <span>NOUVEAU MAIL</span>
              <span></span>
            </div>
            
            {lines.map((line, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1.5fr_1.5fr_32px] gap-2">
                <Input
                  value={line.ref_vente}
                  onChange={(e) => updateLine(idx, 'ref_vente', e.target.value)}
                  onPaste={idx === 0 ? handlePaste : undefined}
                  placeholder="Ex: 635867081"
                  className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                  data-testid={`email-ref-${idx}`}
                />
                <Input
                  value={line.old_email}
                  onChange={(e) => updateLine(idx, 'old_email', e.target.value)}
                  placeholder="ancien@mail.com"
                  className="bg-zinc-800 border-zinc-700 text-sm"
                  data-testid={`email-old-${idx}`}
                />
                <Input
                  value={line.new_email}
                  onChange={(e) => updateLine(idx, 'new_email', e.target.value)}
                  placeholder="nouveau@mail.com"
                  className="bg-zinc-800 border-zinc-700 text-sm"
                  data-testid={`email-new-${idx}`}
                />
                <button
                  onClick={() => removeLine(idx)}
                  className="text-zinc-600 hover:text-red-400 transition-colors"
                  disabled={lines.length <= 1}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}

            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLine}
                className="border-zinc-700 text-zinc-400 hover:text-white"
              >
                <Plus className="h-4 w-4 mr-1" /> Ajouter une ligne
              </Button>

              <Button
                onClick={handlePreview}
                disabled={validLines.length === 0 || loading}
                className="bg-sky-600 hover:bg-sky-500 text-white"
                data-testid="email-preview-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Vérifier ({validLines.length} ligne{validLines.length > 1 ? 's' : ''})
              </Button>
            </div>
          </div>
        )}

        {/* Preview Results */}
        {preview && !result && (
          <div className="space-y-3" data-testid="email-preview-results">
            {/* Summary */}
            <div className="flex items-center gap-4 p-3 bg-zinc-800 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-emerald-400 font-bold">{preview.summary.ok} OK</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-amber-400 font-bold">{preview.summary.warning} A vérifier</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-red-400 font-bold">{preview.summary.error} Erreur</span>
              </div>
            </div>

            {/* Detail lines */}
            <div className="space-y-2 max-h-[45vh] overflow-y-auto">
              {preview.results.map((r, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "p-3 rounded-lg border text-sm",
                    r.status === 'OK' && "bg-emerald-500/5 border-emerald-500/30",
                    r.status === 'WARNING' && "bg-amber-500/5 border-amber-500/30",
                    r.status === 'ERROR' && "bg-red-500/5 border-red-500/30"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {r.status === 'OK' && <Check className="h-5 w-5 text-emerald-400" />}
                      {r.status === 'WARNING' && <AlertTriangle className="h-5 w-5 text-amber-400" />}
                      {r.status === 'ERROR' && <X className="h-5 w-5 text-red-400" />}
                      <span className="font-mono font-bold text-white">{r.ref_vente}</span>
                    </div>
                    <span className={cn(
                      "text-xs px-2 py-0.5 rounded font-bold",
                      r.status === 'OK' && "bg-emerald-500/20 text-emerald-400",
                      r.status === 'WARNING' && "bg-amber-500/20 text-amber-400",
                      r.status === 'ERROR' && "bg-red-500/20 text-red-400"
                    )}>
                      {r.status === 'OK' ? 'PRET' : r.status === 'WARNING' ? 'A VERIFIER' : 'ERREUR'}
                    </span>
                  </div>
                  <div className="mt-2 ml-8 space-y-1">
                    <p className="text-zinc-400">
                      <span className="text-zinc-500">Ancien :</span> <span className="text-red-300">{r.old_email || '(vide)'}</span>
                      <span className="text-zinc-600 mx-2">→</span>
                      <span className="text-zinc-500">Nouveau :</span> <span className="text-emerald-300">{r.new_email}</span>
                    </p>
                    <p className="text-zinc-500 text-xs">{r.message}</p>
                    {r.tickets && r.tickets.length > 0 && (
                      <div className="mt-1 text-xs text-zinc-500">
                        {r.tickets.map((t, ti) => (
                          <span key={ti} className="inline-block mr-3">
                            {t.evenement?.substring(0, 35)} | {t.source || '-'} | Qty: {t.qty || 1}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-between pt-2 border-t border-zinc-700">
              <Button
                variant="outline"
                onClick={() => setPreview(null)}
                className="border-zinc-700 text-zinc-400"
              >
                Retour (modifier)
              </Button>
              <Button
                onClick={handleApply}
                disabled={preview.summary.ok === 0 || applying}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="email-apply-btn"
              >
                {applying ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Appliquer les {preview.summary.ok} ligne(s) OK
              </Button>
            </div>
          </div>
        )}

        {/* Success Result */}
        {result && (
          <div className="space-y-4" data-testid="email-update-result">
            <div className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-center">
              <Check className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-emerald-400 font-bold text-lg">{result.message}</p>
              <div className="mt-3 space-y-1 text-sm text-zinc-400">
                {result.details?.map((d, i) => (
                  <p key={i}>
                    <span className="font-mono">{d.ref_vente}</span> : {d.updated} ticket(s) mis à jour
                  </p>
                ))}
              </div>
            </div>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={handleReset} className="border-zinc-700 text-zinc-400">
                Nouvelle mise à jour
              </Button>
              <Button onClick={() => onOpenChange(false)} className="bg-zinc-700 hover:bg-zinc-600">
                Fermer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
